import { useEffect, useMemo, useState } from 'react'

import { servicesRing } from '../../data/bookingServices'
import type { ServiceSlice } from '../../types'
import { buildDonutSegments, tweenNumber } from '../../utils/chartMath'
import { cn } from '../../utils/cn'
import { absoluteBox } from '../../utils/geometry'

const strokeWidth = 35
const radius = 64
const circumference = 2 * Math.PI * radius
const INNER_FILL = '#474766'

type ServiceDonutChartProps = {
  slices: ServiceSlice[]
  totalCount: number
  activeId: string | null
  onActiveChange: (id: string | null) => void
}

export const ServiceDonutChart = ({
  slices,
  totalCount,
  activeId,
  onActiveChange,
}: ServiceDonutChartProps) => {
  const [drawn, setDrawn] = useState(false)
  const [displayCount, setDisplayCount] = useState(0)

  const segments = useMemo(
    () =>
      buildDonutSegments(
        slices.map(({ id, percent, color }) => ({ id, percent, color })),
        circumference,
      ),
    [slices],
  )

  const activeSlice = slices.find((slice) => slice.id === activeId) ?? null
  const centerValue = activeSlice ? activeSlice.count : totalCount
  const centerCaption = activeSlice ? activeSlice.label : 'Total'
  const centerDetail = activeSlice ? `${activeSlice.percent}%` : null

  useEffect(() => {
    setDrawn(false)
    const reveal = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(reveal)
  }, [slices])

  useEffect(() => {
    const from = displayCount
    const to = centerValue
    const duration = 420
    const start = performance.now()

    let frame = 0
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      setDisplayCount(tweenNumber(from, to, progress))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- animate from the last rendered count
  }, [centerValue])

  return (
    <>
      <svg
        aria-label="Booking services breakdown"
        className="absolute overflow-visible"
        style={absoluteBox(servicesRing)}
        role="img"
        viewBox={`0 0 ${servicesRing.width} ${servicesRing.height}`}
      >
        <g transform={`translate(${servicesRing.width / 2} ${servicesRing.height / 2})`}>
          {segments.map((segment, index) => {
            const dimmed = activeId !== null && activeId !== segment.id
            const boosted = activeId === segment.id

            return (
              <circle
                key={segment.id}
                cx={0}
                cy={0}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={boosted ? strokeWidth + 5 : strokeWidth}
                strokeDasharray={`${drawn ? segment.length : 0} ${circumference}`}
                strokeDashoffset={-segment.offset}
                transform="rotate(-90)"
                className={cn(
                  'cursor-pointer transition-[stroke-width,opacity] duration-300 motion-reduce:transition-none',
                  dimmed ? 'opacity-35' : 'opacity-100',
                )}
                style={{
                  transition: `stroke-dasharray 700ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 80}ms, stroke-width 300ms, opacity 300ms`,
                  filter: boosted ? 'drop-shadow(0 0 8px rgba(255,255,255,0.4))' : undefined,
                  ...(segment.color === '#FFFFFF' ? { stroke: '#FFFFFF' } : {}),
                }}
                onMouseEnter={() => onActiveChange(segment.id)}
                onMouseLeave={() => onActiveChange(null)}
              />
            )
          })}

          <circle
            cx={0}
            cy={0}
            r={radius - strokeWidth / 2 - 1}
            fill={INNER_FILL}
            className="pointer-events-none"
          />
        </g>
      </svg>

      <CenterReadout
        caption={centerCaption}
        value={displayCount}
        detail={centerDetail}
        active={Boolean(activeSlice)}
      />
    </>
  )
}

type CenterReadoutProps = {
  caption: string
  value: number
  detail: string | null
  active: boolean
}

const CenterReadout = ({ caption, value, detail, active }: CenterReadoutProps) => (
  <div
    className="pointer-events-none absolute text-center"
    style={absoluteBox({ x: 110, y: 168, width: 98, height: 48 })}
  >
    <p
      className={cn(
        'truncate font-host text-[11px] font-semibold leading-normal text-white transition-all duration-300 motion-reduce:transition-none',
        active ? 'scale-105 text-dash-chart-label' : 'scale-100',
      )}
    >
      {caption}
    </p>
    <p
      className={cn(
        'mt-[2px] font-host text-[13px] font-semibold leading-normal text-white transition-all duration-300 motion-reduce:transition-none',
        active && 'text-[15px]',
      )}
    >
      {value.toLocaleString()}
    </p>
    {detail ? (
      <p className="mt-[1px] font-host text-[9px] font-medium leading-normal text-dash-chart-label">
        {detail}
      </p>
    ) : null}
  </div>
)

type SliceLabelsProps = {
  slices: ServiceSlice[]
  activeId: string | null
  onActiveChange: (id: string | null) => void
}

export const ServiceSliceLabels = ({
  slices,
  activeId,
  onActiveChange,
}: SliceLabelsProps) => (
  <>
    {slices.map((slice) => (
      <SliceLabel
        key={slice.id}
        slice={slice}
        active={activeId === slice.id}
        dimmed={activeId !== null && activeId !== slice.id}
        onEnter={() => onActiveChange(slice.id)}
        onLeave={() => onActiveChange(null)}
      />
    ))}
  </>
)

type SliceLabelProps = {
  slice: ServiceSlice
  active: boolean
  dimmed: boolean
  onEnter: () => void
  onLeave: () => void
}

const SliceLabel = ({ slice, active, dimmed, onEnter, onLeave }: SliceLabelProps) => (
  <div
    className={cn(
      'transition-opacity duration-300 motion-reduce:transition-none',
      dimmed ? 'opacity-40' : 'opacity-100',
    )}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
  >
    <p
      className={cn(
        'absolute cursor-default whitespace-nowrap font-host text-[12.444px] font-semibold leading-normal text-white transition-transform duration-300 motion-reduce:transition-none',
        active && 'scale-105',
      )}
      style={absoluteBox({ x: slice.label_x, y: slice.label_y })}
    >
      {slice.label}
    </p>
    <p
      className={cn(
        'absolute cursor-default whitespace-nowrap font-host text-[8.889px] font-medium leading-normal text-white transition-transform duration-300 motion-reduce:transition-none',
        active && 'scale-105 text-[10px]',
      )}
      style={absoluteBox({ x: slice.share_x, y: slice.share_y })}
    >
      {slice.share}
    </p>
  </div>
)
